import { useState } from 'react';
import { ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const Step3AccountCreation = ({ sessionId, onComplete, onBack }) => {
  const { register } = useAuth();
  const { lang } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = lang === 'de' ? 'Name ist erforderlich' : 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = lang === 'de' ? 'E-Mail ist erforderlich' : 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = lang === 'de' ? 'Ungültige E-Mail-Adresse' : 'Invalid email address';
    }

    if (!formData.password) {
      newErrors.password = lang === 'de' ? 'Passwort ist erforderlich' : 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = lang === 'de' ? 'Passwort muss mindestens 8 Zeichen haben' : 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = lang === 'de' ? 'Passwörter stimmen nicht überein' : 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      await register(formData.name, formData.email, formData.password, sessionId);
      toast.success(lang === 'de' ? 'Account erfolgreich erstellt' : 'Account created successfully');
      onComplete();
    } catch (err) {
      const message = err.response?.data?.detail || (lang === 'de' ? 'Registrierung fehlgeschlagen' : 'Registration failed');
      toast.error(message);
      if (message.toLowerCase().includes('e-mail') || message.toLowerCase().includes('email')) {
        setErrors(prev => ({ ...prev, email: message }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="step3-form">
      <h2 className="text-3xl font-semibold tracking-tight mb-3 text-balance">
        {lang === 'de' ? 'Erstelle deinen Account' : 'Create your account'}
      </h2>
      <p className="text-muted-foreground mb-8">
        {lang === 'de'
          ? 'Deine Analysen werden automatisch mit deinem Account verknüpft.'
          : 'Your analyses will be linked to your account automatically.'}
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="name">{lang === 'de' ? 'Name' : 'Name'}</Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder={lang === 'de' ? 'Dein Name' : 'Your name'}
            value={formData.name}
            onChange={handleChange}
            className={`h-12 bg-secondary/50 border-transparent focus:border-primary rounded-lg ${
              errors.name ? 'border-red-500' : ''
            }`}
            data-testid="register-name-input"
          />
          {errors.name && (
            <p className="text-sm text-red-400">{errors.name}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">{lang === 'de' ? 'E-Mail' : 'Email'}</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder={lang === 'de' ? 'deine@email.de' : 'your@email.com'}
            value={formData.email}
            onChange={handleChange}
            className={`h-12 bg-secondary/50 border-transparent focus:border-primary rounded-lg ${
              errors.email ? 'border-red-500' : ''
            }`}
            data-testid="register-email-input"
          />
          {errors.email && (
            <p className="text-sm text-red-400">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">{lang === 'de' ? 'Passwort' : 'Password'}</Label>
          <Input
            id="password"
            name="password"
            type="password"
            placeholder={lang === 'de' ? 'Mindestens 8 Zeichen' : 'At least 8 characters'}
            value={formData.password}
            onChange={handleChange}
            className={`h-12 bg-secondary/50 border-transparent focus:border-primary rounded-lg ${
              errors.password ? 'border-red-500' : ''
            }`}
            data-testid="register-password-input"
          />
          {errors.password && (
            <p className="text-sm text-red-400">{errors.password}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">{lang === 'de' ? 'Passwort bestätigen' : 'Confirm password'}</Label>
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            placeholder={lang === 'de' ? 'Passwort wiederholen' : 'Repeat password'}
            value={formData.confirmPassword}
            onChange={handleChange}
            className={`h-12 bg-secondary/50 border-transparent focus:border-primary rounded-lg ${
              errors.confirmPassword ? 'border-red-500' : ''
            }`}
            data-testid="register-confirm-password-input"
          />
          {errors.confirmPassword && (
            <p className="text-sm text-red-400">{errors.confirmPassword}</p>
          )}
        </div>

        <div className="flex gap-3 pt-4">
          <Button 
            type="button"
            variant="outline"
            onClick={onBack}
            className="rounded-full border-border"
            data-testid="step3-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {lang === 'de' ? 'Zurück' : 'Back'}
          </Button>
          <Button 
            type="submit"
            disabled={isSubmitting}
            className="flex-1 rounded-full gradient-button text-white h-12 disabled:opacity-50"
            data-testid="create-account-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {lang === 'de' ? 'Wird erstellt...' : 'Creating...'}
              </>
            ) : (
              <>
                {lang === 'de' ? 'Account erstellen' : 'Create account'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Step3AccountCreation;
